import { API_ORIGIN, apiUrl } from "./apiOrigin";
import React, { useEffect, useState } from "react";
import InspectorSOPStaffGrid, { assignmentCompliance } from "./InspectorSOPStaffGrid";
import { InspectorMhraSite } from "./MhraAlerts";
const link = {
  color: "#0958D9",
  background: "#E6F4FF",
  border: "1px solid #91CAFF",
  borderRadius: 6,
  padding: "10px 14px",
  cursor: "pointer",
  textAlign: "left",
  fontWeight: 700,
  width: "100%",
  marginBottom: 8
};
const date = x => x ? new Date(x).toLocaleDateString("en-GB") : "Not recorded";
// Sort a copy so the API data stays unchanged: read first, then unread;
// alphabetical within each group for a predictable staff list.
export function sortInspectorStaff(people = []) {
  const read = p => !!p.signedAt || p.status === "signed";
  return [...people].sort((a, b) => Number(read(b)) - Number(read(a)) || String(a.name || "").localeCompare(String(b.name || "")));
}
export function InspectorSopPopup({
  sop,
  onClose
}) {
  const [host, setHost] = useState(null);
  const shortDate = x => x ? new Date(x).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit"
  }) : "Not recorded";
  const filePath = String(sop.fileUrl || "").split(/[?#]/)[0];
  const office = /\.(docx?|pptx?|xlsx?)$/i.test(filePath) || /\.(docx?|pptx?|xlsx?)$/i.test(sop.filename || "");
  const previewUrl = office ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(sop.fileUrl)}` : sop.fileUrl;
  useEffect(() => {
    const win = host?.ownerDocument.defaultView;
    if (!win) return undefined;
    const key = e => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    win.addEventListener("keydown", key);
    return () => win.removeEventListener("keydown", key);
  }, [host, onClose]);
  return <div ref={setHost} role="dialog" aria-modal="true" aria-label={sop.title} style={{
    position: "fixed",
    inset: 0,
    zIndex: 12000,
    background: "#0008",
    padding: "3vh 3vw",
    overflow: "auto"
  }}>
    <section style={{
      background: "white",
      padding: 20,
      borderRadius: 10
    }}>
      <header style={{
        position: "sticky",
        top: 0,
        background: "white",
        zIndex: 1,
        paddingBottom: 12,
        borderBottom: "1px solid #ddd"
      }}>
        <button onClick={onClose} style={{
          float: "right"
        }}>Close SOP</button><h2 style={{
          marginTop: 0
        }}>{sop.title}</h2>
        <div style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px 28px",
          fontSize: 14
        }}>
          <span><strong>Review date:</strong> {date(sop.reviewDate || sop.expiryDate)}</span>
          <span><strong>Version:</strong> {sop.version !== "" && sop.version != null ? sop.version : "Not recorded"}</span>
          <span><strong>Reviewed by:</strong> {sop.reviewedByName || "Not recorded"}</span>
        </div>
      </header>
      {sop.fileUrl ? <><iframe title={`${sop.title} contents`} src={previewUrl} style={{
          width: "100%",
          height: "60vh",
          border: "1px solid #ddd",
          marginTop: 12
        }} /><p><a data-no-viewer="1" href={sop.fileUrl} target="_blank" rel="noreferrer" title="Open or download the original SOP document">Open original document</a></p>
        {office && <p style={{
          fontSize: 12,
          color: "#595959"
        }}>If the Office preview cannot access this file, use the original document link.</p>}</> : <p>No document is recorded for this SOP.</p>}
      <h3>All assigned staff — reading and reminders</h3>
      {!(sop.people || []).length ? <p>No staff assignments recorded.</p> : <table style={{
        width: "100%",
        borderCollapse: "collapse",
        fontSize: 14
      }}>
        <thead><tr><th style={{
              textAlign: "left",
              padding: 8
            }}>Staff member</th><th style={{
              textAlign: "left",
              padding: 8
            }}>Reading status</th></tr></thead>
        <tbody>{sortInspectorStaff(sop.people || []).map((p, i) => <tr key={p.staffId || i} style={{
            background: p.signedAt ? "#F6FFED" : "#FFF1F0"
          }}>
          <td style={{
              padding: 8,
              borderTop: "1px solid #ddd"
            }}>{p.name}{p.left ? " (no longer active)" : ""}</td>
          <td style={{
              padding: 8,
              borderTop: "1px solid #ddd",
              color: p.signedAt ? "#237804" : "#A8071A"
            }}>{p.signedAt ? `Read on ${shortDate(p.signedAt)}` : p.lastReminderSentAt ? `Reminded on ${shortDate(p.lastReminderSentAt)}` : "Not read — no reminder recorded"}</td>
        </tr>)}</tbody></table>}
    </section></div>;
}
export default function InspectorResources({
  branch,
  token
}) {
  const [sop, setSop] = useState(false),
    [mhra, setMhra] = useState(false);
  const [reading, setReading] = useState({
    loading: true,
    pct: null,
    error: ""
  });
  useEffect(() => {
    const controller = new AbortController();
    setReading({
      loading: true,
      pct: null,
      error: ""
    });
    (async () => {
      try {
        const response = await fetch(apiUrl("/sop_compliance_grid"), {
          method: "POST",
          signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token || localStorage.getItem("authToken")}`
          },
          body: JSON.stringify({
            asOfDate: new Date().toISOString().slice(0, 10),
            groupBy: "pharmacy",
            pharmacyId: branch.dbName
          })
        });
        if (!response.ok) throw new Error("Reading percentage unavailable");
        const data = await response.json();
        if (controller.signal.aborted) return;
        const group = data.groups?.[0];
        const total = assignmentCompliance(group?.cells || {}, data.sopTitles || [], group?.headers || []);
        setReading({
          loading: false,
          ...total,
          error: ""
        });
      } catch (e) {
        if (e.name !== "AbortError") setReading({
          loading: false,
          pct: null,
          error: e.message
        });
      }
    })();
    return () => controller.abort();
  }, [branch.dbName, token]);
  return <div>
    <button style={link} title={`Click to expand the branch SOP list and staff reading grid. ${reading.loading ? "Loading reading percentage…" : reading.error || (reading.pct == null ? "No assigned readings" : `${reading.didRead} of ${reading.shouldHaveRead} assigned readings completed`)}`} aria-expanded={sop} onClick={() => setSop(!sop)}>
      <span style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 100px",
        gap: 12,
        alignItems: "center",
        width: "100%",
        maxWidth: 644
      }}>
        <span>{sop ? "▾" : "▸"} SOP list and reading grid</span>
        <span aria-live="polite" style={{
          textAlign: "center",
          padding: "3px 8px",
          borderRadius: 5,
          fontSize: 12,
          background: reading.pct == null ? "#F5F5F5" : reading.pct >= 85 ? "#D9F7BE" : reading.pct >= 65 ? "#FFF1B8" : "#FFCCC7",
          color: "#262626"
        }}>{reading.loading ? "Loading…" : reading.error ? "Unavailable" : reading.pct == null ? "No readings" : `${reading.pct}% read`}</span>
      </span>
    </button>
    {sop && <InspectorSOPStaffGrid pharmacyId={branch.dbName} asOfDate={new Date().toISOString().slice(0, 10)} token={token} />}
    <button style={link} title="Click to see MHRA alerts, recorded actions and staff acknowledgements" aria-expanded={mhra} onClick={() => setMhra(!mhra)}>{mhra ? "▾" : "▸"} MHRA alerts</button>
    {mhra && <InspectorMhraSite dbName={branch.dbName} pharmacyName={branch.pharmacyName} onClose={() => setMhra(false)} />}
  </div>;
}
